import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { ticketObjectStatus } from "../../constants/ticketStatus";
import HeadPayment from "./headPayment";
import DetailPayment from "./detailPayment";
import Totals from "./totals";
import { FormActions } from "../../components/ui/form";
import PaymentsMethods from "./paymentsMethods";
import { ErrorModal, ConfirmModal } from "../../components/ModalAlert";
import supabase from "../../supabase";
import { LoaderSpinner } from "../../components/spinner/loader";
import { formatDateForDb } from "../../utils/dateUtils";
import OrderDetailsView from "../orderDetailsView/index";

const empty = (value) => {
  return value === undefined || value === null || value === '';
};

const ClientBillDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state;
  const receivedClient = location.state?.client;
  const [payment, setPayment] = useState({});
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [client, setClient] = useState(null);
  const [ticket, setTicket] = useState({});
  const [ticketDetails, setTicketDetails] = useState({});
  const [deletePrevColumnsId, setDeletePrevColumnsId] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalExpensesWithIva, setTotalExpensesWithIva] = useState(0);
  const [totalIva210, setTotalIva210] = useState(0);
  const [totalIva105, setTotalIva105] = useState(0);
  const [totalDue, setTotalDue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [disableEdition, setDisableEdition] = useState(false);
  const [totalPaymentMethods, setTotalPaymentMethods] = useState(0);
  const [usedBalance, setUsedBalance] = useState(0);
  const totalBalance = receivedClient ? receivedClient.balance : 0;
  const [description, setDescription] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cashId] = useState(1);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [checks, setChecks] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [isFromBillDetails, setIsFromBillDetails] = useState(false);

  const handleClientSelection = (selectedClient) => {
    setClient(selectedClient);
  };

  useEffect(() => {
    if (data?.ticket) {
      if (
        data?.ticket.status === ticketObjectStatus.cancelada ||
        data?.ticket.status === ticketObjectStatus.pagada
      ) {
        setDisableEdition(true);
      }
      setTicket(data?.ticket);
      setTicketDetails(data?.ticket.ticketDetails || data?.ticket.details);
    }
  }, [data]);

  useEffect(() => {
    const fetchTotalTickets = async () => {
      try {
        if (!receivedClient) {
          console.error('No se recibió información del cliente');
          return;
        }

        const { data, error } = await supabase
          .from('tickets')
          .select('ticketId, ticketDetails(*)')
          .eq('status', 2)
          .eq('clientId', receivedClient.clientId);
        if (error) {
          throw new Error('Error al obtener los tickets');
        } else {
          const ticketIds = (data || []).map((t) => t.ticketId).filter(Boolean);
          const rateByTicketId = new Map();
          if (ticketIds.length > 0) {
            const { data: ordersData, error: ordersErr } = await supabase
              .from('orders')
              .select('ticketId, currencyRate')
              .in('ticketId', ticketIds);
            if (!ordersErr && ordersData) {
              ordersData.forEach((o) => {
                if (o.ticketId == null) return;
                const r = parseFloat(o.currencyRate);
                rateByTicketId.set(
                  o.ticketId,
                  !isNaN(r) && r > 0 ? r : 1
                );
              });
            }
          }

          let totalDue = 0;
          (data || []).forEach((ticket) => {
            const rate = rateByTicketId.get(ticket.ticketId) ?? 1;
            (ticket.ticketDetails || []).forEach((detail) => {
              const mult =
                detail.currency === 1 || detail.currency === '1' ? 1 : rate;
              totalDue +=
                detail.quantity *
                detail.price *
                (1 + detail.ivaPercentage / 100) *
                mult;
            });
          });
          setTotalDue(totalDue);
          setTotalTickets(data.length);
        }
      } catch (error) {
        console.error('Error al obtener los tickets:', error);
      }
    };

    fetchTotalTickets();
  }, []);

  const saveOperation = async () => {
    // Validar que haya facturas seleccionadas
    if (selectedTickets.length === 0) {
      ErrorModal({ title: "Error", text: "Debe seleccionar al menos una factura para pagar." });
      return;
    }

    // Validar cheques y construir mensaje de error detallado
    let checksErrorMessage = '';
    const allChecksValid = checks.length === 0 || checks.every((check, index) => {
      const errors = [];
      
      if (!check.amount || check.amount <= 0) {
        errors.push('Monto');
      }
      if (empty(check.number)) {
        errors.push('Número');
      }
      if (empty(check.bank)) {
        errors.push('Banco');
      }
      if (empty(check.branch)) {
        errors.push('Sucursal');
      }
      if (empty(check.issueDate)) {
        errors.push('Fecha de Emisión');
      }
      if (empty(check.paymentDate)) {
        errors.push('Fecha de Pago');
      } else if (!empty(check.issueDate) && new Date(check.issueDate) > new Date(check.paymentDate)) {
        errors.push('Fecha de Pago (debe ser igual o posterior a Fecha de Emisión)');
      }
      if (empty(check.signer)) {
        errors.push('Firmante');
      }
      if (empty(check.cuit)) {
        errors.push('CUIT');
      }
      
      if (errors.length > 0) {
        checksErrorMessage += `\n\nCheque ${index + 1}:\n- ${errors.join('\n- ')}`;
        return false;
      }
      return true;
    });

    const totalToPay = parseFloat((totalExpensesWithIva - totalPaymentMethods).toFixed(2));
    const totalValid = totalToPay <= 0;

    if (totalValid && totalToPay < 0) {
      ConfirmModal({ 
        title: "Atención", 
        text: "El monto ingresado es mayor al monto a pagar. Se computará el monto excedente como saldo a favor para el cliente",
        onConfirm: () => procesarGuardado(allChecksValid, totalValid, checksErrorMessage),
      });
      return;
    } else if (totalValid && totalToPay === 0) {
      procesarGuardado(allChecksValid, totalValid, checksErrorMessage);
    } else if (!totalValid) {
      ErrorModal({ title: "Error", text: "Atención: La suma de los montos ingresados son insuficientes." });
    }
  };

  const procesarGuardado = async (allChecksValid, totalValid, checksErrorMessage) => {
    if (allChecksValid && totalValid) {
      if (disableEdition) {
        navigate("/bills-per-client");
        return;
      }
      setLoading(true);

      // Crear una nueva fila en la tabla payments
      const { data: paymentData, error: paymentError } = await supabase
        .from("payments")
        .insert([
          {
            createdAt: new Date(),
            totalAmount: totalPaymentMethods,
            description: paymentDescription
          },
        ])
        .select("paymentId");

      if (paymentError) {
        ErrorModal({ title: "Error creando el pago", text: paymentError.message });
        setLoading(false);
        return;
      }

      const paymentId = paymentData[0].paymentId;

      // Crear filas en paymentsDetails y actualizar el saldo de la caja
      let paymentDetailsToInsert = Object.keys(paymentMethods).map(methodKey => {
        const method = parseFloat(paymentMethods[methodKey]);
        const paymentMethodId = Object.keys(paymentMethods).indexOf(methodKey);
        if (methodKey !== 'cheques') {
          return {
            paymentId: paymentId,
            paymentMethod: paymentMethodId,
            amount: isNaN(method) ? 0 : method,
          };
        }
      }).filter(detail => detail && detail.amount > 0);

      checks.forEach(check => {
          paymentDetailsToInsert.push({
            paymentId: paymentId,
            paymentMethod: Object.keys(paymentMethods).indexOf('cheques'),
            amount: parseFloat(check.amount),
            checkNumber: check.number,
            checkBank: check.bank,
            checkAmount: parseFloat(check.amount),
            checkEmitionDate: formatDateForDb(check.issueDate),
            checkPaymentDate: formatDateForDb(check.paymentDate),
            checkIssuerName: check.signer,
            checkIssuerCuit: check.cuit,
            checkTransferredName: check.transferredName,
            checkTransferredCuit: check.transferredCuit,
            checkState: 0,
            checkNotes: check.notes,
          });
      });

      if (paymentDetailsToInsert.length > 0) {
        const { error: detailsError } = await supabase
          .from("paymentDetails")
          .insert(paymentDetailsToInsert);

        if (detailsError) {
          ErrorModal({ title: "Error creando los detalles del pago", text: detailsError.message });
          setLoading(false);
          return;
        }
        
      } else {
        ErrorModal({ title: "Error", text: "No hay detalles para crear" });
        setLoading(false);
        return;
      }

      // Crear fila en paymentsTickets y almacenar el nuevo paymentsTicketId
      const newPaymentsTicketIds = [];
      for (const ticketId of selectedTickets) {
        const { data: ticketData, error: ticketError } = await supabase
          .from("paymentsTicket")
          .insert({
            ticketId: ticketId,
            paymentId: paymentId,
          })
          .select("paymentTicketId");

        if (ticketError) {
          ErrorModal({ title: "Error creando la relación de pago " + paymentId + " con el ticket " + ticketId, text: ticketError.message });
          setLoading(false);
          return;
        }

        // Verificar que ticketData no sea null y tenga datos
        if (ticketData && ticketData.length > 0) {
          newPaymentsTicketIds.push(ticketData[0].paymentTicketId);
        } else {
          ErrorModal({ title: "Error", text: "No se pudo obtener el ID del ticket de pago." });
          setLoading(false);
          return;
        }
      }

      // Guardar en cashMovements
      const cashMovementsToInsert = paymentDetailsToInsert
        .filter(detail => detail.paymentMethod !== 0) // Excluir saldo del cliente
        .map(detail => ({
          cashId: detail.paymentMethod,
          amount: detail.amount,
          paymentTicketId: newPaymentsTicketIds[0],
          paymentMethod: detail.paymentMethod,
          createdAt: new Date(),
        }));

      if (cashMovementsToInsert.length > 0) {
        const { error: cashMovementError } = await supabase
          .from("cashMovements")
          .insert(cashMovementsToInsert);

        if (cashMovementError) {
          ErrorModal({ title: "Error creando los movimientos de caja", text: cashMovementError.message });
          setLoading(false);
          return;
        }

        // Actualizar el balance de la caja por cada cashMovementsToInsert
        for (const movement of cashMovementsToInsert) {
          const { data: cashData, error: cashBalanceError } = await supabase
            .from("cash")
            .select("balance")
            .eq("cashId", movement.cashId)
            .single();

          if (cashBalanceError) {
            ErrorModal({ title: "Error actualizando el balance de la caja", text: cashBalanceError.message });
            setLoading(false);
            return;
          }

          const updatedBalance = cashData.balance + movement.amount;
          const { error: updateError } = await supabase
            .from("cash")
            .update({ balance: updatedBalance })
            .eq("cashId", movement.cashId);

          if (updateError) {
            ErrorModal({ title: "Error actualizando el balance de la caja", text: updateError.message });
            setLoading(false);
            return;
          }
        }
      }

      // Actualizar el saldo del cliente si se usó saldo a favor o hay excedente
      const usedBalanceAmount = paymentMethods.saldoFavor ? parseFloat(paymentMethods.saldoFavor) : 0;
      const totalToPayCalc = parseFloat((totalExpensesWithIva - totalPaymentMethods).toFixed(2));
      const excedente = totalToPayCalc < 0 ? Math.abs(totalToPayCalc) : 0;
      let newBalance = Number(totalBalance || 0) - Number(usedBalanceAmount || 0) + excedente;

      // Solo actualizar si el saldo cambió
      if (newBalance !== totalBalance) {
        const { error: clientError } = await supabase
          .from("clients")
          .update({ balance: newBalance })
          .eq("clientId", receivedClient.clientId);

        if (clientError) {
          ErrorModal({ title: "Error actualizando el saldo del cliente", text: clientError.message });
          setLoading(false);
          return;
        }
      }

      // Actualizar el estado de los tickets a pagado
      for (const ticketId of selectedTickets) {
        const { error: updateTicketError } = await supabase
          .from("tickets")
          .update({ status: ticketObjectStatus.pagada })
          .eq("ticketId", ticketId);

        if (updateTicketError) {
          ErrorModal({ 
            title: "Error actualizando el estado del ticket", 
            text: updateTicketError.message 
          });
          setLoading(false);
          return;
        }
      }

      navigate(-1);
      setLoading(false);
    } else if (!allChecksValid) {
      ErrorModal({ 
        title: "Error en Cheques", 
        text: `Por favor, complete todos los campos obligatorios:${checksErrorMessage}` 
      });
      setLoading(false);
    } else if (!totalValid) {
      ErrorModal({ title: "Error", text: "Alerta! La suma de los montos ingresados son insuficientes." });
      setLoading(false);
    }
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", minWidth: 0 }}>
      <HeadPayment
        client={receivedClient}
        setPayment={setPayment}
        disableEdition={disableEdition}
        onClientSelect={handleClientSelection}
        setPaymentDescription={setPaymentDescription}
        totalTickets={totalTickets}
        totalDue={totalDue}
      />
      <DetailPayment
        payment={payment}
        paymentDetails={paymentDetails}
        clientId={receivedClient.clientId}
        ticket={ticket}
        ticketDetails={ticketDetails}
        setPaymentDetails={setPaymentDetails}
        setTotalExpenses={setTotalExpenses}
        setTotalExpensesWithIva={setTotalExpensesWithIva}
        setTotalIva210={setTotalIva210}
        setTotalIva105={setTotalIva105}
        disableEdition={disableEdition}
        setDeletePrevColumnsId={setDeletePrevColumnsId}
        selectedTickets={selectedTickets} 
        setSelectedTickets={setSelectedTickets}
      />
      <Totals
        totalExpenses={totalExpenses}
        totalExpensesWithIva={totalExpensesWithIva}
        iva21={totalIva210}
        iva105={totalIva105}
        totalPaymentMethods={totalPaymentMethods.toFixed(2)}
        setTotalPaymentMethods={setTotalPaymentMethods}
        totalBalance={totalBalance}
        usedBalance={usedBalance}
      />
      {selectedTickets.length > 0 && (
        <PaymentsMethods
          totalExpenses={totalExpenses}
          totalExpensesWithIva={totalExpensesWithIva}
          totalPaymentMethods={totalPaymentMethods.toFixed(2)}
          iva21={totalIva210}
          iva105={totalIva105}
          setTotalPaymentMethods={setTotalPaymentMethods}
          totalBalance={totalBalance}
          setUsedBalance={setUsedBalance}
          setPaymentMethods={setPaymentMethods}
          setChecks={setChecks}
        />
      )}
      <FormActions>
        <Button
          variant="outlined"
          sx={{ minHeight: 44 }}
          onClick={() => {
            const navigationState = {
              order: {
                ...ticket,
                orderDetails: ticketDetails
              }
            };
            navigate("/order-details-form", { state: navigationState });
          }}
        >
          Ver detalles
        </Button>
        <Button variant="outlined" sx={{ minHeight: 44 }} onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveOperation}
          sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 200 }}
        >
          Guardar pago
        </Button>
      </FormActions>
    </Box>
  );
};

export default ClientBillDetails;
